import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import SyncStatus from './SyncStatus';
import WorkActPrompt from './WorkActPrompt';
import './Layout.css';

const NAV_GROUPS = [
  {
    label: 'Работа',
    items: [
      { to: '/tasks', label: 'Задачи', icon: 'tasks' },
      { to: '/calendar', label: 'Календарь', icon: 'calendar' },
      { to: '/dashboard', label: 'Обзор', icon: 'dashboard' },
      { to: '/clients', label: 'Клиенты', icon: 'clients' },
      { to: '/objects', label: 'Объекты', icon: 'objects' },
    ],
  },
  {
    label: 'База',
    items: [
      { to: '/warehouse', label: 'Склад', icon: 'warehouse' },
      { to: '/documents', label: 'Документы', icon: 'documents' },
      { to: '/notes', label: 'Заметки', icon: 'notes' },
    ],
  },
  {
    label: 'Аналитика',
    items: [
      { to: '/finances', label: 'Отчёты', icon: 'reports' },
      { to: '/analytics', label: 'Показатели', icon: 'analytics' },
    ],
  },
  {
    label: 'Система',
    items: [
      { to: '/settings', label: 'Настройки', icon: 'settings' },
    ],
  },
];

const FALLBACK_ROUTES = {
  '/tasks/incomplete': '/tasks',
  '/dashboard': '/tasks',
  '/calendar': '/tasks',
  '/clients': '/tasks',
  '/objects': '/tasks',
  '/warehouse': '/tasks',
  '/documents': '/tasks',
  '/notes': '/tasks',
  '/finances': '/tasks',
  '/analytics': '/tasks',
  '/profile': '/tasks',
  '/settings': '/tasks',
};

const PAGE_TITLES = [
  { match: (path) => path === '/', title: 'Задачи' },
  { match: (path) => path.startsWith('/tasks/incomplete'), title: 'Невыполненные' },
  { match: (path) => path.startsWith('/tasks'), title: 'Задачи' },
  { match: (path) => path.startsWith('/dashboard'), title: 'Обзор' },
  { match: (path) => path.startsWith('/calendar'), title: 'Календарь' },
  { match: (path) => path.startsWith('/clients'), title: 'Клиенты' },
  { match: (path) => path.startsWith('/objects'), title: 'Объекты' },
  { match: (path) => path.startsWith('/warehouse'), title: 'Склад' },
  { match: (path) => path.startsWith('/documents'), title: 'Документы' },
  { match: (path) => path.startsWith('/notes'), title: 'Заметки' },
  { match: (path) => path.startsWith('/finances'), title: 'Отчёты' },
  { match: (path) => path.startsWith('/analytics'), title: 'Показатели' },
  { match: (path) => path.startsWith('/profile'), title: 'Профиль' },
  { match: (path) => path.startsWith('/settings'), title: 'Настройки' },
];

const NavIcon = ({ name }) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'dashboard':
      return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
    case 'tasks':
      return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="m8 12 2.2 2.2L16 8.5" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>;
    case 'clients':
      return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'objects':
      return <svg {...common}><path d="M3 21h18M5 21V5l7-3v19M19 21V9l-7-2" /><path d="M8 7h1M8 11h1M8 15h1M15 12h1M15 16h1" /></svg>;
    case 'warehouse':
      return <svg {...common}><path d="m3 9 9-6 9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" /><path d="M7 21v-8h10v8M7 17h10" /></svg>;
    case 'documents':
      return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M8 13h8M8 17h8" /></svg>;
    case 'notes':
      return <svg {...common}><path d="M4 4h16v16H4zM8 8h8M8 12h8M8 16h5" /></svg>;
    case 'reports':
      return <svg {...common}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 12h6M9 16h6" /></svg>;
    case 'analytics':
      return <svg {...common}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>;
    case 'settings':
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15 1.7 1.7 0 0 0 3.08 14H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63 1.7 1.7 0 0 0 10 3.08V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9 1.7 1.7 0 0 0 20.92 10H21v4h-.08A1.7 1.7 0 0 0 19.4 15Z" /></svg>;
    default:
      return null;
  }
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Определяем, является ли устройство мобильным
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 ||
                            (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) ||
                            window.location.protocol === 'capacitor:' ||
                            window.location.protocol === 'file:';
      setIsMobile(isMobileDevice);
      // На мобильных устройствах меню закрыто по умолчанию
      if (isMobileDevice) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Закрываем меню при изменении маршрута на мобильных устройствах
  useEffect(() => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const handleNativeMenuClose = () => setIsMenuOpen(false);
    window.addEventListener('crm:close-mobile-menu', handleNativeMenuClose);
    return () => {
      window.removeEventListener('crm:close-mobile-menu', handleNativeMenuClose);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const handleBack = useCallback(() => {
    const waitingDialog = document.querySelector('.waiting-dialog-backdrop');
    if (waitingDialog) {
      window.dispatchEvent(new CustomEvent('crm:native-back'));
      return;
    }

    const taskForm = document.querySelector('.task-form-modal');
    if (taskForm) {
      window.dispatchEvent(new CustomEvent('crm:native-back'));
      return;
    }

    if (isMenuOpen) {
      setIsMenuOpen(false);
      return;
    }

    const historyIndex = window.history?.state?.idx;
    if (Number.isInteger(historyIndex) && historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(FALLBACK_ROUTES[location.pathname] || '/tasks');
    }
  }, [isMenuOpen, location.pathname, navigate]);

  useEffect(() => {
    const handleBackRequest = () => handleBack();
    window.addEventListener('crm:request-back', handleBackRequest);
    return () => window.removeEventListener('crm:request-back', handleBackRequest);
  }, [handleBack]);

  const isActive = (item) => item.exact
    ? location.pathname === item.to
    : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const userName = user?.display_name || user?.name || 'Пользователь';
  const userInitial = userName.trim().charAt(0).toUpperCase() || 'S';
  const currentPageTitle = PAGE_TITLES.find((page) => page.match(location.pathname))?.title || 'SAVEL’EV CRM';
  const usesInternalPageScroll = ['/tasks', '/dashboard'].includes(location.pathname);

  return (
    <div className={`layout ${isMenuOpen ? 'menu-is-open' : 'menu-is-closed'}`}>
      {isMobile && (
        <header className="mobile-app-bar">
          <div className={`mobile-app-bar-side ${!['/', '/tasks'].includes(location.pathname) ? 'mobile-app-bar-side-with-back' : ''}`}>
            {!['/', '/tasks'].includes(location.pathname) && (
              <button
                type="button"
                className="mobile-back-button"
                onClick={handleBack}
                aria-label="Назад"
                title="Назад"
              >
                <span className="mobile-back-arrow">←</span>
                <span>Назад</span>
              </button>
            )}

            <button
              type="button"
              className="brand-menu-button mobile-brand-menu-button"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
              aria-expanded={isMenuOpen}
              title={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            >
              <img src="/logo-192.png" alt="" />
            </button>

            <h1 className="mobile-page-title">{currentPageTitle}</h1>
          </div>
        </header>
      )}

      {!isMobile && !isMenuOpen && (
        <button
          type="button"
          className="brand-menu-button desktop-brand-menu-button"
          onClick={toggleMenu}
          aria-label="Открыть меню"
          aria-expanded="false"
          title="Открыть меню"
        >
          <img src="/logo-192.png" alt="" />
        </button>
      )}

      {isMobile && isMenuOpen && (
        <div
          className="menu-overlay"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      <nav className={`sidebar ${isMenuOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button
            type="button"
            className="sidebar-brand sidebar-brand-button"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Свернуть меню' : 'Открыть меню'}
            aria-expanded={isMenuOpen}
            title={isMenuOpen ? 'Свернуть меню' : 'Открыть меню'}
          >
            <img src="/logo-192.png" alt="" className="sidebar-logo" />
            <div>
              <h1>SAVEL'EV</h1>
              <span>CRM</span>
            </div>
          </button>
        </div>

        <Link to="/profile" className="sidebar-profile" onClick={closeMenu}>
          <span className="sidebar-avatar">{userInitial}</span>
          <span className="sidebar-profile-copy">
            <strong>{userName}</strong>
            <small>Открыть профиль</small>
          </span>
          <span className="sidebar-profile-arrow">›</span>
        </Link>

        <div className="sidebar-navigation">
          {NAV_GROUPS.map((group) => (
            <section className="sidebar-group" key={group.label}>
              <h2>{group.label}</h2>
              <ul className="sidebar-menu">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={isActive(item) ? 'active' : ''}
                      onClick={closeMenu}
                      aria-current={isActive(item) ? 'page' : undefined}
                    >
                      <span className="sidebar-menu-icon"><NavIcon name={item.icon} /></span>
                      <span>{item.label}</span>
                      <span className="sidebar-menu-chevron">›</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="sidebar-footer">
          <SyncStatus />
          <button onClick={handleLogout} className="logout-button">
            <span className="logout-icon" aria-hidden="true">↪</span>
            <span>Выйти из аккаунта</span>
          </button>
        </div>
      </nav>

      <main className={`main-content ${isMobile && isMenuOpen ? 'menu-open' : ''} ${usesInternalPageScroll ? 'main-content--internal-scroll' : ''}`}>
        <div className="main-content-outlet">
          <Outlet />
        </div>
      </main>
      <WorkActPrompt />
    </div>
  );
};

export default Layout;
